import {
  ActionStore,
  createAction,
  createGetter,
  createMutation,
  Dictionary,
  KeyStore,
  LazyRemoteCollectionStore,
  mutate,
  on,
  ParamsStore,
  select,
  SOFTLINE_SERVICE_UUID,
  StoreFeature,
  SubscriptionStore,
} from '@softline/core';
import { lastValueFrom } from 'rxjs';
import { AuftragResourceService } from '../services/auftrag-resource.service';
import { SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN } from '../transport.shared';
import { Auftrag } from '../types/auftrag';
import { AuftragDetail } from '../types/auftrag-detail';
import * as StationStore from './station.store';

export interface State extends LazyRemoteCollectionStore.State<Auftrag> {
  details: Dictionary<AuftragDetail>;
}
const store = LazyRemoteCollectionStore.create<Auftrag>();

export const getters = {
  ...store.getters,
  entityWithDetail: createGetter<State, Auftrag & AuftragDetail, number | string>(
    'entityWithDetail'
  ),
};

export const mutations = {
  ...store.mutations,
  addOrUpdateDetail: createMutation<State, AuftragDetail>('addOrUpdateDetail'),
};

export const actions = {
  ...store.actions,
  loadDetail: createAction<State, { id: number }, AuftragDetail>('auftrag/loadSignature'),
  complete: createAction<State, { auftrag: Auftrag; token?: string }, Auftrag>(
    'auftrag/complete'
  ),
};

export const feature: StoreFeature<State> = {
  initialState: {
    ...store.feature.initialState,
    details: {},
  },
  getters: [
    ...store.feature.getters,
    select(getters.entityWithDetail, ({ state, params, get }) => {
      const auftrag = state.entities[params];
      if (!auftrag) return undefined;
      return { ...auftrag, ...(state.details[params] ?? {}) };
    }),
  ],
  mutations: [
    ...store.feature.mutations,
    mutate(mutations.addOrUpdateDetail, ({ state, params }) => {
      const details = { ...state.details };
      details[params.id] = params;
      return { ...state, details };
    }),
  ],
  actions: [
    ...store.feature.actions,
    on(actions.loadDetail, async ({ injector, commit, featureName, params }) => {
      const service = injector.get(AuftragResourceService);
      const token = injector.get(SOFTLINE_SERVICE_UUID)();
      commit(featureName, KeyStore.mutations.add, token);
      commit(featureName, ParamsStore.mutations.add, { key: token, params });

      const subscription$ = SubscriptionStore.handleSubscriptionState(
        service.loadDetails(params.id),
        featureName,
        commit,
        token
      );
      const result = await lastValueFrom(
        ActionStore.handleObservableActionState(
          subscription$,
          featureName,
          commit,
          actions.loadDetail.name,
          token
        )
      );

      if (result) {
        commit(SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN, StationStore.mutations.clear);
      }

      commit(featureName, mutations.addOrUpdateDetail, result);
      commit(
        SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
        StationStore.mutations.addOrUpdateMany,
        result.stationen
      );

      return result;
    }),
    on(actions.complete, async ({ featureName, commit, params, injector }) => {
      const service = injector.get(AuftragResourceService);
      const token = params?.token ?? injector.get(SOFTLINE_SERVICE_UUID)();
      commit(featureName, KeyStore.mutations.add, token);
      commit(featureName, ParamsStore.mutations.add, { key: token, params });

      const subscription$ = SubscriptionStore.handleSubscriptionState(
        service.complete(params.auftrag),
        featureName,
        commit,
        token
      );
      const result = await lastValueFrom(
        ActionStore.handleObservableActionState(
          subscription$,
          featureName,
          commit,
          actions.complete.name,
          token
        )
      );

      commit(featureName, mutations.remove, result);
      return result;
    }),
  ],
};
